import type { Goal } from "@/src/features/api/TrackerApi";
import { goalQueryKeys } from "./queryKeys";
import { isNotFoundError } from "@/src/utils/apiErrors";
import { useOspApiClientProvider } from "@/src/hooks";
import { useQuery } from "@tanstack/react-query";

export interface UseGoalDetailResult {
  data: Goal | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  notFound: boolean;
}

/**
 * Goal 详情页数据 Hook。
 * - 未登录时不请求（enabled 依赖 isLoggedIn）
 * - 404 → notFound
 */
export function useGoalDetail(
  goalId: string,
  isLoggedIn: boolean,
): UseGoalDetailResult {
  const { trackerApi } = useOspApiClientProvider();
  const query = useQuery({
    queryKey: goalQueryKeys.detail(goalId),
    enabled: !!goalId && isLoggedIn && !!trackerApi,
    queryFn: async (): Promise<Goal> => {
      const res = await trackerApi!.goals.getGoal(goalId, {
        enrich_trackers: true,
      });
      const goal = res.data?.data;
      if (!goal) {
        throw new Error("Goal detail response missing data");
      }
      return goal;
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    notFound:
      isNotFoundError(query.error) ||
      (query.isSuccess && query.data === undefined),
  };
}
